import clsx from 'clsx';
import {
  useContext,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useIntl } from 'react-intl';

import { PinIcon, PinOffIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../buttons/button/Button.js';
import { virtualFocus } from '../../../core/styles/virtualFocus.sty.js';
import { getVirtualFocusStyles } from '../../../core/utils/getVirtualFocusStyles.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import { FilterFieldContext } from '../contexts/FilterFieldContext.js';
import {
  usePinnedFilterMutation,
  usePinnedFilters,
} from '../hooks/recent-and-pinned/usePinnedFilters.js';
import { useFilterFieldContext } from '../hooks/useFilterFieldContext.js';
import { filterFieldMessages } from '../messages.js';

export function useSuggestionPin(filterString: string) {
  const { filterNamespace } = useContext(FilterFieldContext);

  const { filters: pinnedFilters, canPin } = usePinnedFilters(filterNamespace);

  const { pinFilter, unpinFilter } = usePinnedFilterMutation(filterNamespace);

  const isPinned =
    pinnedFilters.find(
      (storedFilter) => storedFilter.filter === filterString.trim(),
    ) !== undefined;

  return {
    isPinned,
    canPin,
    pinFilter,
    unpinFilter,
    limitReached: pinnedFilters.length >= 10,
  };
}

/**
 * @internal
 */
export function SuggestionPin(
  props: Readonly<{
    size?: 'condensed' | 'default';
    id: string;
    role?: string;
    filterString: string;
    className?: string;
    placement: 'top' | 'bottom' | 'left' | 'right';
    fallbackPlacements: Array<'top' | 'bottom' | 'left' | 'right'>;
    disabled?: boolean;
    'aria-disabled'?: boolean;
    showError?: boolean;
  }>,
) {
  const {
    id,
    filterString,
    className,
    size,
    placement,
    fallbackPlacements,
    role,
    disabled,
    'aria-disabled': ariaDisabledProps,
    showError,
    ...remainingProps
  } = props;

  const intl = useIntl();

  const { focusedListItem } = useFilterFieldContext();

  const { isPinned, canPin, pinFilter, unpinFilter, limitReached } =
    useSuggestionPin(filterString);

  const pinUnpinTooltip = intl.formatMessage(
    filterFieldMessages.suggestions.pinUnpinTooltip,
    { isPinned },
  );
  const pinUnpinLabel = intl.formatMessage(
    filterFieldMessages.suggestions.pinUnpinLabel,
    { isPinned, filterString },
  );
  const disabledTooltip = intl.formatMessage(
    filterFieldMessages.suggestions.pinUnpinLimitTooltip,
  );

  const ariaDisabled = limitReached && !isPinned;

  // Handle tooltips for virtual focus elements
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  useEffect(() => {
    const classListObserver = new MutationObserver((entries) => {
      for (const entry of entries) {
        if (
          entry.target === buttonRef.current &&
          entry.target instanceof HTMLElement
        ) {
          if (entry.target.classList.contains(virtualFocus.neutral)) {
            setTooltipOpen(true);
          } else {
            setTooltipOpen(false);
          }
        }
      }
    });
    if (buttonRef.current) {
      classListObserver.observe(buttonRef.current, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => {
      classListObserver.disconnect();
    };
  }, []);

  if (!canPin) {
    return null;
  }

  return (
    <Tooltip
      open={!disabled && tooltipOpen}
      onOpenChange={setTooltipOpen}
      placement={placement}
      fallbackPlacements={fallbackPlacements}
      text={ariaDisabled ? disabledTooltip : pinUnpinTooltip}
    >
      <Button
        size={size}
        role={role}
        ref={buttonRef}
        id={id}
        disabled={disabled}
        {...remainingProps}
        data-suggestion-pin
        // When the pin button is rendered in the filter field suffix buttons, aria-selected is not a valid attribute
        // and needs to be omitted to avoid a11y violations.
        {...(role === 'option'
          ? {
              'aria-selected': focusedListItem?.id === id,
              'aria-checked': isPinned,
            }
          : { 'aria-pressed': isPinned })}
        aria-label={ariaDisabled ? disabledTooltip : pinUnpinLabel}
        aria-disabled={ariaDisabledProps || ariaDisabled}
        color={showError ? 'critical' : 'neutral'}
        className={clsx(
          className,
          getVirtualFocusStyles(focusedListItem?.id === id).className,
        )}
        // Need to prevent onPointerDown here in order to prevent the suggestion to be applied by bubbling pointer events.
        onPointerDown={(event: ReactPointerEvent<HTMLButtonElement>) => {
          event.preventDefault();
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        }}
        onClick={() => {
          if (ariaDisabled) {
            return;
          }
          if (isPinned) {
            unpinFilter(filterString);
          } else {
            pinFilter(filterString);
          }
        }}
      >
        {isPinned ? <PinOffIcon /> : <PinIcon />}
      </Button>
    </Tooltip>
  );
}
